export type ISuccessApiResponse<
  TData = unknown,
  TMeta = unknown,
  TMessage extends string = string,
> = {
  message?: TMessage;
  data: TData;
  meta: TMeta;
  status: 'SUCCESS';
};
export type IErrorApiResponse<
  TError = unknown,
  TMessage extends string = string,
  TMeta = unknown,
> = {
  message: TMessage;
  errors: Array<TError>;
  status: 'ERROR';
  meta: TMeta;
};

type IBulkPatchRequest = {
  id: string;
  update: Record<string, unknown>;
};
export type IBulkPatchPayload<TOperation = IBulkPatchRequest> = {
  requests: Array<TOperation>;
};

export type IValidationError<
  ErrorMessage extends string = string,
  TContext extends Record<string, unknown> = Record<string, unknown>,
> = {
  propertyPath: string;
  value: unknown;
  errors: Array<ErrorMessage>;
  context?: TContext;
};
export type ValidationErrorResponse<ErrorMessage extends string = string> =
  IErrorApiResponse<IValidationError<ErrorMessage>>;

// Pagination
export type SortDirection = 'ASC' | 'DESC' | undefined;
export interface ISorter {
  sortField: string;
  sortDirection: SortDirection;
}

export type FilterOperation = 'and' | 'or';
export type FieldOperator =
  | 'eq'
  | 'range'
  | 'neq'
  | 'contains'
  | 'in'
  | 'elemMatch'
  | 'nelemMatch';

export type IFilterCondition = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  column?: string;
  operator?: FieldOperator | '';
};
export interface IFilter {
  operator: FilterOperation;
  query?: string;
  conditions: IFilterCondition[];
}

export type IMultiFieldSearchQuery = {
  searchableFields: Array<string>;
  searchValue: string;
  displayedField: string;
  limit?: number;
};

export const ITEMS_PER_PAGE = [10, 20, 50, 100] as const;
export type ItemsPerPage = (typeof ITEMS_PER_PAGE)[number];

export type IPaginationQuery<TCriteria = IFilter> = {
  itemsPerPage: ItemsPerPage;
  page: number;
  criteria: TCriteria;
  sort:
    | undefined
    | {
        sortField: string;
        sortDirection: SortDirection;
      };
};

export interface IPaginationMeta {
  currentPage: number;
  from: number;
  to?: number;
  hasMore: boolean;
  perPage: number;
  total: number;
}

export type IPaginationResponse<
  TData = unknown,
  TAdditionalMeta = unknown,
> = ISuccessApiResponse<Array<TData>, IPaginationMeta & TAdditionalMeta>;

export type ICancellationOptions = { signal: AbortSignal };
